import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Cropper } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Cameraswitch,
  Close,
  ZoomIn,
  ZoomOut,
  PhotoCamera,
} from '@mui/icons-material';
import uploadIcon from '../../images/upload.png';

const DEFAULT_MAX_SIZE = 500 * 1024;
const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
];
const ACCEPTED_FILE_INPUT =
  'image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
const CROP_ZOOM_IN_RATIO = 1.2;
const CROP_ZOOM_OUT_RATIO = 0.8;

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImage = async (file) => {
  const image = new Image();
  image.src = await readAsDataUrl(file);

  return new Promise((resolve, reject) => {
    image.onload = () => resolve(image);
    image.onerror = reject;
  });
};

const dataUrlToBlob = (dataUrl) => {
  const [header, data] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: mime });
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    if (!canvas.toBlob) {
      resolve(dataUrlToBlob(canvas.toDataURL(type, quality)));
      return;
    }

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Unable to create image from canvas.'));
          return;
        }
        resolve(blob);
      },
      type,
      quality
    );
  });

const getOutputImageType = (type) => {
  if (type === 'image/png' || type === 'image/webp') {
    return type;
  }

  return 'image/jpeg';
};

const getOutputImageExtension = (type) => {
  if (type === 'image/png') {
    return '.png';
  }
  if (type === 'image/webp') {
    return '.webp';
  }

  return '.jpg';
};

const toUploadContentType = (type) =>
  type === 'image/jpeg' ? 'image/jpg' : type;

const normalizeHeaderName = (name) => name.toLowerCase();

const getPresignedUploadUrl = (presignedPayload) =>
  presignedPayload?.uploadUrl ||
  presignedPayload?.presignedUrl ||
  presignedPayload?.signedUrl ||
  presignedPayload?.url;

const getUploadedFileUrl = (presignedPayload) =>
  presignedPayload?.imageUrl ||
  presignedPayload?.fileUrl ||
  presignedPayload?.publicUrl ||
  presignedPayload?.downloadUrl ||
  presignedPayload?.objectUrl ||
  presignedPayload?.key ||
  presignedPayload?.url;

const normalizePresignedPayload = (payload) => {
  if (typeof payload === 'string') {
    return {
      uploadUrl: payload,
      fileUrl: payload.split('?')[0],
      method: 'PUT',
    };
  }

  const uploadUrl = getPresignedUploadUrl(payload);
  const fileUrl = getUploadedFileUrl(payload) || uploadUrl?.split('?')[0];

  return {
    ...payload,
    uploadUrl,
    fileUrl,
    headers: payload?.requiredHeaders || payload?.headers,
    method: payload?.method || 'PUT',
  };
};

const hasCameraPermissionDenied = async () => {
  if (!navigator.permissions?.query) {
    return false;
  }

  try {
    const permission = await navigator.permissions.query({ name: 'camera' });
    return permission.state === 'denied';
  } catch (e) {
    return false;
  }
};

const buildResizedBlob = async (file, maxSize) => {
  const image = await loadImage(file);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const outputType = getOutputImageType(file.type);
  const outputExtension = getOutputImageExtension(outputType);
  let scale = 1;
  let quality = outputType === 'image/png' ? 1 : 0.86;
  let blob = file;

  for (let attempt = 0; attempt < 14; attempt += 1) {
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    blob = await canvasToBlob(canvas, outputType, quality);
    if (blob?.size <= maxSize) {
      return new File([blob], file.name.replace(/\.[^.]+$/, outputExtension), {
        type: outputType,
      });
    }

    if (['image/jpeg', 'image/webp'].includes(outputType) && quality > 0.58) {
      quality -= 0.08;
    } else {
      scale *= 0.82;
    }
  }

  throw new Error('Image is still larger than 500KB after resize.');
};

const compressImage = async (file, maxSize) => {
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Only JPG, PNG, and WEBP images are supported.');
  }

  if (file.size <= maxSize) {
    return file;
  }

  const resizedFile = await buildResizedBlob(file, maxSize);
  if (resizedFile.size > maxSize) {
    throw new Error('Image is still larger than 500KB after resize.');
  }

  return resizedFile;
};

const uploadToPresignedUrl = async (file, presignedPayload) => {
  const headers = Object.entries({
    'Content-Type': toUploadContentType(file.type),
    ...(presignedPayload.headers || {}),
  }).reduce((acc, [name, value]) => {
    if (normalizeHeaderName(name) !== 'content-length') {
      acc[name] = value;
    }
    return acc;
  }, {});

  const response = await fetch(presignedPayload.uploadUrl, {
    method: presignedPayload.method,
    headers,
    body: file,
  });

  if (!response.ok) {
    throw new Error(
      `Image upload failed. Storage returned ${response.status} ${response.statusText}.`
    );
  }
};

const cropImageFile = async ({ file, cropper }) => {
  const canvas = cropper.getCanvas();
  if (!canvas) {
    return file;
  }

  const outputType = getOutputImageType(file.type);
  const outputExtension = getOutputImageExtension(outputType);
  const blob = await canvasToBlob(canvas, outputType, 0.92);
  return new File([blob], file.name.replace(/\.[^.]+$/, outputExtension), {
    type: outputType,
  });
};

const getFullImageCoordinates = ({ imageSize }) => ({
  height: imageSize.height,
  left: 0,
  top: 0,
  width: imageSize.width,
});

const ImageCaptureUpload = ({
  backgroundColor,
  disabled,
  getPresignedUrl,
  helperText,
  label,
  maxSize,
  onChange,
  onClear,
  onError,
  onUploaded,
  previewUrl,
  required,
  requiredText,
  skipUpload,
}) => {
  const cropperRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastUploadedUrlRef = useRef('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [isBusy, setIsBusy] = useState(false);
  const [localPreview, setLocalPreview] = useState(previewUrl);
  const [cropOpen, setCropOpen] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const [cropImageUrl, setCropImageUrl] = useState('');
  const [cropEdited, setCropEdited] = useState(false);
  const [cropperReady, setCropperReady] = useState(false);

  const attachCameraStream = useCallback((node = videoRef.current) => {
    if (!node || !streamRef.current) {
      return;
    }

    if (node.srcObject !== streamRef.current) {
      node.srcObject = streamRef.current;
    }
    node.play?.().catch(() => {});
  }, []);

  const stopCamera = () => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraStream(null);
    setCameraReady(false);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const reportError = (message) => {
    if (onError) {
      onError(message);
    }
  };

  const startCamera = async (nextFacingMode = facingMode) => {
    if (!navigator.mediaDevices?.getUserMedia) {
      openFilePicker();
      return;
    }

    if (await hasCameraPermissionDenied()) {
      reportError(
        'Camera permission is blocked. Enable camera permission to capture an image.'
      );
      return;
    }

    try {
      setIsBusy(true);
      setCameraReady(false);
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: nextFacingMode } },
      });
      streamRef.current = stream;
      setCameraStream(stream);
      setCameraOpen(true);
      setFacingMode(nextFacingMode);
      setTimeout(() => attachCameraStream(), 0);
      setTimeout(() => attachCameraStream(), 250);
    } catch (e) {
      const noCamera = ['NotFoundError', 'OverconstrainedError'].includes(
        e?.name
      );
      if (noCamera) {
        openFilePicker();
      } else {
        reportError('Camera permission is required to capture an image.');
      }
    } finally {
      setIsBusy(false);
    }
  };

  const closeCamera = () => {
    stopCamera();
    setCameraOpen(false);
  };

  const closeCrop = () => {
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
    }
    setCropOpen(false);
    setCropFile(null);
    setCropImageUrl('');
    setCropEdited(false);
    setCropperReady(false);
  };

  const prepareCrop = async (file) => {
    try {
      setIsBusy(true);
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        throw new Error('Only JPG, PNG, and WEBP images are supported.');
      }

      await loadImage(file);
      const imageUrl = URL.createObjectURL(file);
      if (cropImageUrl) {
        URL.revokeObjectURL(cropImageUrl);
      }
      setCropFile(file);
      setCropImageUrl(imageUrl);
      setCropEdited(false);
      setCropperReady(false);
      setCropOpen(true);
      closeCamera();
    } catch (e) {
      reportError(e?.message || 'Unable to read image.');
    } finally {
      setIsBusy(false);
    }
  };

  const uploadFile = async (file) => {
    const compressedFile = await compressImage(file, maxSize);
    const nextPreview = await readAsDataUrl(compressedFile);

    if (skipUpload) {
      lastUploadedUrlRef.current = '';
      setLocalPreview(nextPreview);
      if (onChange) {
        onChange(compressedFile);
      }
      if (onUploaded) {
        onUploaded('', {
          file: compressedFile,
          presigned: null,
        });
      }
      closeCamera();
      return;
    }

    const presignedPayload = normalizePresignedPayload(
      await getPresignedUrl(compressedFile)
    );

    if (!presignedPayload.uploadUrl) {
      throw new Error('Missing presigned upload URL.');
    }

    await uploadToPresignedUrl(compressedFile, presignedPayload);

    lastUploadedUrlRef.current = presignedPayload.fileUrl;
    setLocalPreview(nextPreview);
    if (onChange) {
      onChange(compressedFile);
    }
    onUploaded(presignedPayload.fileUrl, {
      file: compressedFile,
      presigned: presignedPayload,
    });
    closeCamera();
  };

  const confirmCropAndUpload = async () => {
    if (!cropFile || !cropperRef.current) {
      return;
    }

    try {
      setIsBusy(true);
      const croppedFile = cropEdited
        ? await cropImageFile({
            file: cropFile,
            cropper: cropperRef.current,
          })
        : cropFile;
      await uploadFile(croppedFile);
      closeCrop();
    } catch (e) {
      reportError(e?.message || 'Unable to upload image.');
    } finally {
      setIsBusy(false);
    }
  };

  const handleFileChange = (event) => {
    const [file] = event.target.files || [];
    event.target.value = '';
    if (file) {
      prepareCrop(file);
    }
  };

  const captureFrame = async () => {
    try {
      const video = videoRef.current;
      if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
        throw new Error('Camera is not ready yet. Please try again.');
      }

      const width = video.videoWidth;
      const height = video.videoHeight;
      if (!width || !height) {
        throw new Error('Camera frame is not available yet. Please try again.');
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(video, 0, 0, width, height);
      const blob = await canvasToBlob(canvas, 'image/jpeg', 0.9);
      const file = new File([blob], `camera-${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });
      prepareCrop(file);
    } catch (e) {
      reportError(e?.message || 'Unable to capture image.');
    }
  };

  const switchCamera = () => {
    const nextFacingMode =
      facingMode === 'environment' ? 'user' : 'environment';
    startCamera(nextFacingMode);
  };

  const updateCropZoom = (ratio) => {
    setCropEdited(true);
    cropperRef.current?.zoomImage(ratio);
  };

  const markCropEdited = () => {
    setCropEdited(true);
  };

  const markCropperReady = (cropper) => {
    if (cropper?.getCanvas?.()) {
      setCropperReady(true);
      return;
    }

    setTimeout(() => {
      if (cropperRef.current?.getCanvas?.()) {
        setCropperReady(true);
      }
    }, 100);
  };

  const clearImage = () => {
    lastUploadedUrlRef.current = '';
    setLocalPreview('');
    if (onChange) {
      onChange(null);
    }
    if (onClear) {
      onClear();
    }
  };

  useEffect(() => {
    if (cameraOpen && cameraStream) {
      attachCameraStream();
    }
  }, [attachCameraStream, cameraOpen, cameraStream]);

  useEffect(() => {
    if (!previewUrl) {
      lastUploadedUrlRef.current = '';
      setLocalPreview('');
      return;
    }

    if (previewUrl !== lastUploadedUrlRef.current) {
      setLocalPreview(previewUrl);
    }
  }, [previewUrl]);

  useEffect(() => () => stopCamera(), []);

  return (
    <Box
      sx={{
        backgroundColor,
        borderRadius: 1,
        p: backgroundColor ? 1.5 : 0,
        padding: 0,
      }}
    >
      <input
        ref={fileInputRef}
        accept={ACCEPTED_FILE_INPUT}
        hidden
        onChange={handleFileChange}
        required={required}
        type="file"
      />
      <Box
        display="flex"
        alignItems="flex-start"
        gap={1.25}
        sx={{ width: '100%' }}
      >
        <Box
          sx={{
            flexShrink: 0,
            position: 'relative',
          }}
        >
          <Box
            aria-label={`Capture ${label}`}
            component="button"
            disabled={disabled || isBusy}
            onClick={() => startCamera()}
            type="button"
            sx={{
              alignItems: 'center',
              backgroundColor: backgroundColor
                ? backgroundColor
                : 'transparent',
              border: localPreview ? '1px solid' : '1px dashed',
              borderColor: localPreview ? 'divider' : 'text.disabled',
              borderRadius: 1,
              cursor: disabled || isBusy ? 'default' : 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              height: 118,
              justifyContent: 'center',
              overflow: 'hidden',
              p: 0,
              width: 118,
            }}
          >
            {isBusy ? (
              <CircularProgress size={28} />
            ) : localPreview ? (
              <Box
                alt={label}
                component="img"
                src={localPreview}
                sx={{
                  height: '100%',
                  objectFit: 'cover',
                  width: '100%',
                }}
              />
            ) : (
              <>
                <PhotoCamera color="disabled" sx={{ fontSize: 34 }} />
                {required && (
                  <Typography
                    color="error"
                    component="span"
                    sx={{ fontSize: 11, lineHeight: 1 }}
                  >
                    *{requiredText}
                  </Typography>
                )}
              </>
            )}
          </Box>
          {localPreview && !disabled && !isBusy && (
            <IconButton
              aria-label={`Clear ${label}`}
              onClick={clearImage}
              size="small"
              sx={{
                backgroundColor: 'background.paper',
                boxShadow: 2,
                height: 22,
                position: 'absolute',
                right: -7,
                top: -7,
                width: 22,
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
              }}
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
        <Box
          display="flex"
          alignItems="flex-start"
          gap={1.5}
          pt={0.5}
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          <IconButton
            aria-label={`Upload ${label}`}
            disabled={disabled || isBusy}
            onClick={openFilePicker}
            sx={{
              borderRadius: 1,
              height: 36,
              p: 0.5,
              width: 36,
            }}
          >
            <Box
              alt=""
              component="img"
              src={uploadIcon}
              sx={{ height: 28, width: 28 }}
            />
          </IconButton>
          <Box
            sx={{
              lineHeight: 1.35,
              pt: 0.5,
            }}
          >
            <Typography
              color="text.secondary"
              display="block"
              variant="caption"
            >
              {helperText}
            </Typography>
            <Typography color="text.primary" display="block" variant="body2">
              Suggested size: 1:1 aspect ratio
            </Typography>
            <Typography color="text.primary" display="block" variant="body2">
              (e.g. 270x270, 320x320)
            </Typography>
          </Box>
        </Box>
      </Box>
      <Dialog open={cameraOpen} onClose={closeCamera} maxWidth="sm" fullWidth>
        <DialogTitle>{label}</DialogTitle>
        <IconButton
          aria-label="Close camera"
          onClick={closeCamera}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close fontSize="small" />
        </IconButton>
        <DialogContent>
          <Box
            component="video"
            ref={(node) => {
              videoRef.current = node;
              attachCameraStream(node);
            }}
            autoPlay
            muted
            onLoadedMetadata={() => {
              setCameraReady(true);
              videoRef.current?.play?.().catch(() => {});
            }}
            onCanPlay={() => {
              setCameraReady(true);
              videoRef.current?.play?.().catch(() => {});
            }}
            playsInline
            sx={{
              backgroundColor: '#000',
              borderRadius: 1,
              display: 'block',
              width: '100%',
            }}
          />
        </DialogContent>
        <DialogActions>
          <IconButton
            aria-label="Switch camera"
            disabled={isBusy}
            onClick={switchCamera}
          >
            <Cameraswitch />
          </IconButton>
          <Button
            disabled={isBusy || !cameraReady}
            onClick={captureFrame}
            variant="contained"
          >
            {isBusy ? <CircularProgress size={20} /> : 'Capture'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cropOpen}
        onClose={() => {
          if (!isBusy) {
            closeCrop();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Crop image</DialogTitle>
        <IconButton
          aria-label="Close crop"
          disabled={isBusy}
          onClick={closeCrop}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close fontSize="small" />
        </IconButton>
        <DialogContent>
          <Box
            sx={{
              backgroundColor: '#111',
              borderRadius: 1,
              margin: '0 auto',
              maxHeight: '65vh',
              maxWidth: 520,
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
            }}
          >
            {cropImageUrl && (
              <Cropper
                ref={cropperRef}
                src={cropImageUrl}
                className="member-photo-cropper"
                defaultCoordinates={getFullImageCoordinates}
                disabled={isBusy}
                imageRestriction="stencil"
                onInteractionStart={markCropEdited}
                onMove={markCropEdited}
                onReady={markCropperReady}
                onResize={markCropEdited}
                onUpdate={markCropperReady}
                stencilProps={{
                  movable: true,
                  resizable: true,
                }}
                style={{
                  background: '#111',
                  height: 360,
                  maxHeight: '65vh',
                  width: '100%',
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
          }}
        >
          <Box display="flex" gap={1}>
            <IconButton
              aria-label="Zoom out"
              disabled={isBusy}
              onClick={() => updateCropZoom(CROP_ZOOM_OUT_RATIO)}
            >
              <ZoomOut />
            </IconButton>
            <IconButton
              aria-label="Zoom in"
              disabled={isBusy}
              onClick={() => updateCropZoom(CROP_ZOOM_IN_RATIO)}
            >
              <ZoomIn />
            </IconButton>
          </Box>
          <Box display="flex" gap={1.5}>
            <Button disabled={isBusy} onClick={closeCrop} variant="outlined">
              Cancel
            </Button>
            <Button
              disabled={isBusy || !cropperReady}
              onClick={confirmCropAndUpload}
              variant="contained"
            >
              {isBusy ? (
                <CircularProgress size={20} />
              ) : skipUpload ? (
                'Use photo'
              ) : (
                'Upload'
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ImageCaptureUpload.defaultProps = {
  backgroundColor: '',
  disabled: false,
  helperText: 'JPG, PNG, or WEBP',
  label: 'Upload image',
  maxSize: DEFAULT_MAX_SIZE,
  onChange: null,
  onClear: null,
  onError: null,
  onUploaded: null,
  previewUrl: '',
  required: false,
  requiredText: 'Required',
  skipUpload: false,
};

ImageCaptureUpload.propTypes = {
  backgroundColor: PropTypes.string,
  disabled: PropTypes.bool,
  getPresignedUrl: PropTypes.func,
  helperText: PropTypes.string,
  label: PropTypes.string,
  maxSize: PropTypes.number,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onError: PropTypes.func,
  onUploaded: PropTypes.func,
  previewUrl: PropTypes.string,
  required: PropTypes.bool,
  requiredText: PropTypes.string,
  skipUpload: PropTypes.bool,
};

export default ImageCaptureUpload;
